/** Trading state store using Zustand — manages positions, orders, alerts, risk, system status. */

import { create } from 'zustand'
import type { Position } from '../types/position'
import type { Order } from '../types/order'
import type { Alert } from '../types/alert'
import type { RiskSnapshot } from '../types/risk'
import type { AutonomyReadiness, SystemStatus, TradingMode } from '../types/system'
import type { Signal } from '../types/signal'
import type { Fill } from '../types/fill'

const MAX_ARRAY_SIZE = 1000

function haltedReadiness(readiness: AutonomyReadiness): AutonomyReadiness {
  return {
    ...readiness,
    readyToAct: false,
    tradingMode: 'halted',
    tradingModeAllowsAction: false,
    blockers: Array.from(new Set([...readiness.blockers, 'Operator emergency stop requested'])),
  }
}

function systemMarkedHalted(system: SystemStatus | null): SystemStatus {
  return {
    overall: 'halted',
    tradingMode: 'halted',
    uptimeSeconds: system?.uptimeSeconds ?? 0,
    services: system?.services ?? [],
    broker: system?.broker ?? {
      connected: false,
      port: 0,
      lastHeartbeat: null,
      pingLatencyMs: null,
      reconnectCountToday: 0,
    },
    autonomyReadiness: system?.autonomyReadiness
      ? haltedReadiness(system.autonomyReadiness)
      : undefined,
    marketDataReadiness: system?.marketDataReadiness,
  }
}

function unknownReadiness(readiness: AutonomyReadiness): AutonomyReadiness {
  return {
    ...readiness,
    readyToAct: false,
    dataFresh: false,
    tradingMode: 'unknown',
    tradingModeKnown: false,
    tradingModeAllowsAction: false,
    brokerConnected: false,
    brokerGateOk: false,
    executionEngineReady: false,
    blockers: Array.from(
      new Set([
        ...readiness.blockers,
        'System status refresh failed; safety state is unknown',
      ]),
    ),
  }
}

function systemMarkedUnknown(system: SystemStatus | null): SystemStatus {
  return {
    overall: 'unknown',
    tradingMode: 'unknown',
    uptimeSeconds: system?.uptimeSeconds ?? 0,
    services:
      system?.services.map((service) => ({
        ...service,
        status: 'unknown',
      })) ?? [],
    broker: {
      connected: false,
      port: system?.broker?.port ?? 0,
      lastHeartbeat: system?.broker?.lastHeartbeat ?? null,
      pingLatencyMs: null,
      reconnectCountToday: system?.broker?.reconnectCountToday ?? 0,
    },
    blockers: Array.from(
      new Set([
        ...(system?.blockers ?? []),
        'System status refresh failed; safety state is unknown',
      ]),
    ),
    autonomyReadiness: system?.autonomyReadiness
      ? unknownReadiness(system.autonomyReadiness)
      : undefined,
    marketDataReadiness: system?.marketDataReadiness
      ? {
          ...system.marketDataReadiness,
          state: 'unknown',
          liveReady: false,
        }
      : undefined,
  }
}

function applySystemSnapshot(current: SystemStatus | null, incoming: SystemStatus): SystemStatus {
  void current
  return incoming
}

// ─── Store Interface ──────────────────────────────────────────────────────

export interface TradingState {
  // Data
  positions: Position[]
  orders: Order[]
  alerts: Alert[]
  signals: Signal[]
  fills: Fill[]
  risk: RiskSnapshot | null
  system: SystemStatus | null

  // Connection
  wsConnected: boolean
  lastUpdate: string | null

  // Actions: set data (typically from WebSocket or REST)
  setPositions: (positions: Position[]) => void
  updatePosition: (id: string, update: Partial<Position>) => void
  removePosition: (id: string) => void

  setOrders: (orders: Order[]) => void
  updateOrder: (id: string, update: Partial<Order>) => void
  addOrder: (order: Order) => void

  setAlerts: (alerts: Alert[]) => void
  addAlert: (alert: Alert) => void
  acknowledgeAlert: (id: string) => void
  resolveAlert: (id: string) => void

  setSignals: (signals: Signal[]) => void
  removeSignal: (id: string) => void

  setFills: (fills: Fill[]) => void
  addFill: (fill: Fill) => void

  setRisk: (risk: RiskSnapshot) => void
  setSystem: (system: SystemStatus) => void
  markSystemHalted: () => void
  markSystemUnknown: () => void
  setWsConnected: (connected: boolean) => void

  // Reset all state (used on logout to prevent stale data leaking)
  reset: () => void

  // Derived selectors
  activePositions: () => Position[]
  pendingOrders: () => Order[]
  unacknowledgedAlerts: () => Alert[]
  criticalAlerts: () => Alert[]
  tradingMode: () => TradingMode
}

// ─── Store ────────────────────────────────────────────────────────────────

export const useTradingStore = create<TradingState>((set, get) => ({
  positions: [],
  orders: [],
  alerts: [],
  signals: [],
  fills: [],
  risk: null,
  system: null,
  wsConnected: false,
  lastUpdate: null,

  // Positions
  setPositions: (positions) => set({ positions, lastUpdate: new Date().toISOString() }),

  updatePosition: (id, update) =>
    set((state) => ({
      positions: state.positions.map((p) => (p.id === id ? { ...p, ...update } : p)),
      lastUpdate: new Date().toISOString(),
    })),

  removePosition: (id) =>
    set((state) => ({
      positions: state.positions.filter((p) => p.id !== id),
      lastUpdate: new Date().toISOString(),
    })),

  // Orders
  setOrders: (orders) => set({ orders, lastUpdate: new Date().toISOString() }),

  updateOrder: (id, update) =>
    set((state) => ({
      orders: state.orders.map((o) => (o.id === id ? { ...o, ...update } : o)),
      lastUpdate: new Date().toISOString(),
    })),

  addOrder: (order) =>
    set((state) => ({
      orders: [...state.orders, order],
      lastUpdate: new Date().toISOString(),
    })),

  // Alerts
  setAlerts: (alerts) => set({ alerts, lastUpdate: new Date().toISOString() }),

  addAlert: (alert) =>
    set((state) => ({
      alerts: [alert, ...state.alerts].slice(0, MAX_ARRAY_SIZE),
      lastUpdate: new Date().toISOString(),
    })),

  acknowledgeAlert: (id) =>
    set((state) => ({
      alerts: state.alerts.map((a) =>
        a.id === id ? { ...a, acknowledgedAt: new Date().toISOString() } : a,
      ),
      lastUpdate: new Date().toISOString(),
    })),

  resolveAlert: (id) =>
    set((state) => ({
      alerts: state.alerts.map((a) =>
        a.id === id ? { ...a, resolvedAt: new Date().toISOString() } : a,
      ),
      lastUpdate: new Date().toISOString(),
    })),

  // Signals
  setSignals: (signals) => set({ signals, lastUpdate: new Date().toISOString() }),

  removeSignal: (id) =>
    set((state) => ({
      signals: state.signals.filter((s) => s.id !== id),
      lastUpdate: new Date().toISOString(),
    })),

  // Fills
  setFills: (fills) => set({ fills, lastUpdate: new Date().toISOString() }),

  addFill: (fill) =>
    set((state) => ({
      fills: [fill, ...state.fills].slice(0, MAX_ARRAY_SIZE),
      lastUpdate: new Date().toISOString(),
    })),

  // Snapshots
  setRisk: (risk) => set({ risk, lastUpdate: new Date().toISOString() }),

  setSystem: (system) =>
    set((state) => ({
      system: applySystemSnapshot(state.system, system),
      lastUpdate: new Date().toISOString(),
    })),

  markSystemHalted: () =>
    set((state) => ({
      system: systemMarkedHalted(state.system),
      lastUpdate: new Date().toISOString(),
    })),

  markSystemUnknown: () =>
    set((state) => ({
      system: systemMarkedUnknown(state.system),
      lastUpdate: new Date().toISOString(),
    })),

  setWsConnected: (connected) => set({ wsConnected: connected }),

  reset: () =>
    set({
      positions: [],
      orders: [],
      alerts: [],
      signals: [],
      fills: [],
      risk: null,
      system: null,
      wsConnected: false,
      lastUpdate: null,
    }),

  // Derived
  activePositions: () =>
    get().positions.filter(
      (p) =>
        p.status === 'active' ||
        p.status === 'approaching_exit' ||
        p.status === 'at_risk' ||
        p.status === 'committed' ||
        p.status === 'reconciliation_required',
    ),

  pendingOrders: () =>
    get().orders.filter(
      (o) => o.state === 'pending' || o.state === 'submitted' || o.state === 'partially_filled',
    ),

  unacknowledgedAlerts: () => get().alerts.filter((a) => !a.acknowledgedAt && !a.resolvedAt),

  criticalAlerts: () => get().alerts.filter((a) => a.severity === 'critical' && !a.resolvedAt),

  tradingMode: () => get().system?.tradingMode ?? 'unknown',
}))
